import { useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import moment from "moment";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import AdminStockAdjustmentModal from "../../../../components/Admin/InvestorPortal/AdminStockAdjustmentModal";
import InvestorPortalDetailHeader from "../../../../components/Admin/InvestorPortal/InvestorPortalDetailHeader";
import {
  DetailSection,
  DetailField,
  DetailFieldList,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalDetailFields";
import InvestorPortalActionButton, {
  InvestorPortalActionStack,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalActionButton";
import {
  ProductStatusBadge,
  StockStatusBadge,
} from "../../../../components/Admin/InvestorPortal/AdminStatusBadge";
import { formatMoney } from "../../../../utils/currencyFormat";
import { convertToTitleCase } from "../../../../utils/helpers";
import { staffMarketplaceProductPath } from "../../../../utils/staffMarketplaceAdapter";
import { getProductLifecycleActions } from "../../../../store/admin/marketplace/productLifecycle";
import { PRODUCT_STATUS } from "../../../../store/admin/marketplace/constants";
import {
  useGetStaffMarketplaceProductQuery,
  useStaffMarketplaceProductLifecycleMutation,
  useAdjustStaffMarketplaceInventoryMutation,
  useListStaffMarketplaceInventoryHistoryQuery,
} from "../../../../store/admin/marketplace/staffMarketplaceApi";
import { withMarketplaceGuard } from "../withInvestorPortalGuard";

function productTargetToAction(target, currentStatus) {
  if (target === PRODUCT_STATUS.INTERNAL_REVIEW) return "submit-review";
  if (target === PRODUCT_STATUS.PUBLISHED) return "publish";
  if (target === PRODUCT_STATUS.UNPUBLISHED) return "unpublish";
  if (target === PRODUCT_STATUS.OUT_OF_STOCK) return "mark-out-of-stock";
  if (target === PRODUCT_STATUS.ARCHIVED) return "archive";
  if (target === PRODUCT_STATUS.DRAFT) {
    return currentStatus === PRODUCT_STATUS.INTERNAL_REVIEW ? "return-to-draft" : "unpublish";
  }
  return null;
}

function ProductDetailPage() {
  const { productSlug } = useParams();
  const productKey = productSlug;
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(null);
  const [stockModalOpen, setStockModalOpen] = useState(false);

  const { data: product, isFetching, isError } = useGetStaffMarketplaceProductQuery(productKey, {
    skip: !productKey,
  });
  const { data: inventoryHistory = [], isFetching: isHistoryLoading } =
    useListStaffMarketplaceInventoryHistoryQuery(productKey, { skip: !productKey });
  const [lifecycleAction, { isLoading: isLifecycleLoading }] =
    useStaffMarketplaceProductLifecycleMutation();
  const [adjustInventory, { isLoading: isAdjusting }] = useAdjustStaffMarketplaceInventoryMutation();

  const lifecycleActions = useMemo(
    () => getProductLifecycleActions(product?.lifecycleStatus || product?.status),
    [product]
  );

  if (isFetching && !product) {
    return (
      <div className="pb-4">
        <TableSkeletonLoader rows={8} columns={2} />
      </div>
    );
  }

  if (!product || isError) {
    return <Navigate to="/super_admin/investor_portal/marketplace/products" replace />;
  }

  const isPublished =
    (product.lifecycleStatus || product.status) === PRODUCT_STATUS.PUBLISHED ||
    (product.lifecycleStatus || product.status) === PRODUCT_STATUS.OUT_OF_STOCK;

  const openPublicPage = () => {
    const slug = product.slug || productKey;
    window.open(`${window.location.origin}/invest/marketplace/${slug}`, "_blank");
  };

  const runLifecycle = (actionDef) => {
    if (actionDef.action === "preview") {
      if (!isPublished) {
        toast.error("Publish this product before viewing the public page.");
        return;
      }
      openPublicPage();
      return;
    }
    const action = productTargetToAction(
      actionDef.target,
      product.lifecycleStatus || product.status
    );
    if (!action) {
      toast.error("This action is not available.");
      return;
    }
    setConfirm({
      title: actionDef.label,
      message: `Are you sure you want to ${actionDef.label.toLowerCase()} "${product.name}"?`,
      confirmText: "Yes, continue",
      confirmButtonClass:
        actionDef.variant === "danger" ? "bg-danger text-white" : "bg-primary1 text-white",
      action: async () => {
        try {
          await lifecycleAction({ id: productKey, action }).unwrap();
          toast.success(actionDef.label);
        } catch (error) {
          toast.error(error?.data?.message || "Lifecycle action failed.");
        }
        setConfirm(null);
      },
    });
  };

  const handleStockAdjust = async ({ quantity, note }) => {
    try {
      await adjustInventory({
        id: productKey,
        body: {
          new_quantity: quantity,
          adjustment_type: "manual_adjustment",
          note: note || undefined,
        },
      }).unwrap();
      toast.success("Stock updated");
    } catch (error) {
      toast.error(error?.data?.message || "Unable to adjust stock.");
      throw error;
    }
  };

  const historyItems = Array.isArray(inventoryHistory)
    ? inventoryHistory
    : inventoryHistory?.items || [];

  return (
    <div className="pb-4">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <InvestorPortalDetailHeader
          title="Product"
          highlight={product.name}
          onBack={() => navigate("/super_admin/investor_portal/marketplace/products")}
        />
        <div className="d-flex flex-wrap gap-2">
          <InvestorPortalActionButton
            btnText="Edit"
            variant="outline"
            fullWidth={false}
            onClick={() => navigate(staffMarketplaceProductPath(product, "/edit"))}
          />
          {isPublished ? (
            <InvestorPortalActionButton
              btnText="View public page"
              variant="ghost"
              fullWidth={false}
              onClick={openPublicPage}
            />
          ) : null}
        </div>
      </div>

      <div className="row g-3 investor-portal-detail-layout">
        <div className="col-lg-8">
          <DetailSection>
            <div className="investor-portal-status-row mb-3">
              <ProductStatusBadge status={product.lifecycleStatus || product.status} />
              <StockStatusBadge status={product.stockStatus} />
            </div>
            <h5 className="fw-bold mb-1" style={{ color: "#383838" }}>
              {product.name}
            </h5>
            <p className="text-muted small mb-4">
              {[product.categoryName, product.subcategoryName].filter(Boolean).join(" › ") ||
                "Uncategorized"}
              {product.sku ? ` · SKU ${product.sku}` : ""}
            </p>

            <DetailFieldList>
              <DetailField label="Price">
                <span className="fw-bold">{formatMoney(product.priceNgn ?? product.price)}</span>
              </DetailField>
              <DetailField label="Unit" value={product.unitLabel || "—"} />
              <DetailField
                label="Stock quantity"
                value={product.stockQuantity != null ? product.stockQuantity : "—"}
              />
              <DetailField
                label="Low stock threshold"
                value={product.lowStockThreshold != null ? product.lowStockThreshold : "—"}
              />
              <DetailField label="Track inventory" value={product.trackInventory ? "Yes" : "No"} />
              <DetailField label="Pre-order allowed" value={product.preOrderAllowed ? "Yes" : "No"} />
              <DetailField label="Delivery" value={product.deliveryAvailable ? "Available" : "No"} />
              <DetailField label="Pickup" value={product.pickupAvailable ? "Available" : "No"} />
              <DetailField
                label="Pickup locations"
                value={
                  (product.pickupLocations || []).length
                    ? product.pickupLocations.join(" · ")
                    : product.pickupLocation || "—"
                }
              />
              <DetailField
                label="Estimated fulfillment"
                value={product.estimatedFulfillmentTime || "—"}
              />
              <DetailField label="Seller" value={product.sellerName || "—"} />
              <DetailField label="Slug" value={product.slug || "—"} />
              <DetailField
                label="Published"
                value={
                  product.publishedAt ? moment(product.publishedAt).format("DD-MMM-YYYY HH:mm") : "—"
                }
              />
              <DetailField label="Created by" value={product.createdByName || "—"} />
            </DetailFieldList>
          </DetailSection>

          {(product.shortDescription || product.fullDescription) && (
            <DetailSection title="Descriptions">
              {product.shortDescription ? (
                <p className="small mb-2" style={{ whiteSpace: "pre-wrap" }}>
                  {product.shortDescription}
                </p>
              ) : null}
              {product.fullDescription ? (
                <p className="text-muted small mb-0" style={{ whiteSpace: "pre-wrap" }}>
                  {product.fullDescription}
                </p>
              ) : null}
            </DetailSection>
          )}

          <DetailSection title="Inventory history">
            {isHistoryLoading ? (
              <TableSkeletonLoader rows={4} columns={3} />
            ) : historyItems.length === 0 ? (
              <p className="text-muted small mb-0">No inventory adjustments yet.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Change</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyItems.map((entry, idx) => (
                      <tr key={entry.id || idx}>
                        <td>
                          {entry.at || entry.created_at
                            ? moment(entry.at || entry.created_at).format("DD-MMM-YYYY HH:mm")
                            : "—"}
                        </td>
                        <td>
                          {entry.quantity_change != null
                            ? entry.quantity_change
                            : entry.new_quantity != null
                              ? `→ ${entry.new_quantity}`
                              : entry.adjustment_type || "—"}
                        </td>
                        <td>{entry.note || entry.reason || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </DetailSection>
        </div>

        <div className="col-lg-4">
          <DetailSection title="Actions">
            <InvestorPortalActionStack>
              <InvestorPortalActionButton
                btnText="Adjust stock"
                variant="teal"
                disabled={isAdjusting}
                onClick={() => setStockModalOpen(true)}
              />
              <InvestorPortalActionButton
                btnText="Edit product"
                variant="outline"
                onClick={() =>
                  navigate(staffMarketplaceProductPath(product, "/edit"))
                }
              />
              {isPublished ? (
                <InvestorPortalActionButton
                  btnText="View public page"
                  variant="ghost"
                  onClick={openPublicPage}
                />
              ) : null}
              {lifecycleActions
                .filter((a) => a.action !== "preview")
                .map((action) => (
                  <InvestorPortalActionButton
                    key={action.label}
                    btnText={action.label}
                    variant={action.variant || "outline"}
                    disabled={isLifecycleLoading}
                    onClick={() => runLifecycle(action)}
                  />
                ))}
            </InvestorPortalActionStack>
            <p className="text-muted small mt-3 mb-0">
              Status:{" "}
              {convertToTitleCase(
                String(product.lifecycleStatus || product.status || "").replace(/_/g, " ")
              )}
            </p>
          </DetailSection>
        </div>
      </div>

      <AdminStockAdjustmentModal
        show={stockModalOpen}
        product={product}
        onClose={() => setStockModalOpen(false)}
        onSubmit={handleStockAdjust}
      />

      <ConfirmationModal
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={confirm?.action}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText}
        confirmButtonClass={confirm?.confirmButtonClass}
        isLoading={isLifecycleLoading}
      />
    </div>
  );
}

export default withMarketplaceGuard(ProductDetailPage);
